import { ReactNode, useId } from 'react';
import { Link } from 'react-router-dom';
import { differenceInCalendarDays, format, isWithinInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Tippy from '@tippyjs/react';
import { FiClock, FiLock } from 'react-icons/fi';
import { IoIosWarning } from 'react-icons/io';

import { Program, PROGRAM_BACKGROUND } from 'components/ui/program-panel';
import { getMonthNameFromString } from 'utils/helpers';

import { SectionHeading } from './section-heading';

type SectionProgram = Exclude<Program, 'reconhecimento'>;

interface ProgramSectionProps {
  program: SectionProgram;
  /** Nome do programa. Vira o título da seção, fora do cartão. */
  name: string;
  /** Chamada em destaque do cabeçalho colorido. */
  headline?: ReactNode;
  description?: ReactNode;
  /** Ilustração decorativa à direita do cabeçalho. */
  illustration?: ReactNode;
  children?: ReactNode;
  /** Botões do rodapé. */
  actions?: ReactNode;
  /** Nota à direita do rodapé (prazo, contagem). */
  note?: ReactNode;
}

/**
 * Painel de programa da Home: o nome do programa como título da seção e, abaixo, o cartão com
 * cabeçalho na cor do programa, corpo branco e rodapé de ações. A cor vem do mesmo mapa do
 * `ProgramPanel`, para os dois nunca divergirem.
 */
export function ProgramSection({
  program,
  name,
  headline,
  description,
  illustration,
  children,
  actions,
  note,
}: ProgramSectionProps) {
  const headingId = useId();
  const hasFooter = !!actions || !!note;
  const hasHeader = !!headline || !!description || !!illustration;

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-3">
      <SectionHeading id={headingId}>{name}</SectionHeading>

      <div className="overflow-hidden rounded-jj-xl bg-brand-white shadow-jj-02">
        {hasHeader && (
          <header
            // D3: 74px, 22px e 26px não têm degrau no Tailwind, e min-h não tem escala de spacing no 3.3.
            className={`${PROGRAM_BACKGROUND[program]} flex min-h-[74px] flex-wrap items-center gap-[22px] px-8 py-[26px] text-content`}
          >
            {/* D3: max-w não tem escala de spacing no Tailwind 3.3. */}
            <div className="min-w-0 max-w-[620px] flex-[1_1_320px]">
              {/* Todo parágrafo aqui declara o próprio `leading-*` contra o reset global do rsuite. */}
              {headline && (
                <p className="mb-3 font-display text-jj-body font-bold leading-snug text-brand-blue-dark">{headline}</p>
              )}

              {description && <div className="text-jj-body-sm leading-relaxed">{description}</div>}
            </div>

            {/* D3: 26px não tem degrau no Tailwind. */}
            {illustration && (
              <div aria-hidden="true" className="-mb-[26px] -mt-2.5 ml-auto mr-4 hidden shrink-0 self-end sm:block">
                {illustration}
              </div>
            )}
          </header>
        )}

        {/* D3: 26px não tem degrau no Tailwind (também no rodapé). */}
        {children && <div className={`flex flex-col gap-6 px-8 pt-[26px] ${hasFooter ? '' : 'pb-[26px]'}`}>{children}</div>}

        {hasFooter && (
          <footer className="flex flex-wrap items-center gap-x-5 gap-y-3 px-8 pb-[26px] pt-6">
            {/* As ações ficam juntas; se não couberem com a nota, a nota desce inteira. */}
            {actions && <div className="flex flex-wrap items-center gap-5">{actions}</div>}
            {note && <span className="ml-auto">{note}</span>}
          </footer>
        )}
      </div>
    </section>
  );
}

export type PhaseState = 'past' | 'active' | 'future';

export interface PhaseStep {
  label: string;
  /** Período da etapa, já formatado ("01/09 a 08/09"), ou `NO_DATE` quando a etapa não tem janela. */
  dateLabel: string;
  state: PhaseState;
  /** Janela da etapa (ms), para o prazo do rodapé. Ausente quando a etapa não tem janela. */
  start?: number;
  finish?: number;
  /** Quanto da janela já passou, de 0 a 1. Só na etapa em andamento: é o preenchimento da barra dela. */
  progress?: number;
}

/** Marca de etapa sem janela definida. */
export const NO_DATE = '—';

const dayMonth = (time: number) => format(new Date(time), 'dd/MM', { locale: ptBR });

/**
 * Monta uma etapa da agenda a partir da janela dela: em andamento se `now` está dentro,
 * finalizada se a janela já fechou, a seguir nos demais casos. Sem janela, `NO_DATE`.
 */
export function phaseStep(label: string, start?: number, finish?: number, now: Date = new Date()): PhaseStep {
  if (!start || !finish) return { label, dateLabel: NO_DATE, state: 'future' };

  const state: PhaseState = isWithinInterval(now, { start: new Date(start), end: new Date(finish) })
    ? 'active'
    : finish < now.getTime()
      ? 'past'
      : 'future';

  const step: PhaseStep = { label, dateLabel: `${dayMonth(start)} a ${dayMonth(finish)}`, state, start, finish };

  return state === 'active' ? { ...step, progress: (now.getTime() - start) / (finish - start) } : step;
}

/** Largura mínima do preenchimento, para a etapa que acabou de abrir não parecer vazia. */
const MIN_PROGRESS_PERCENT = 4;

/** Largura do preenchimento da barra da etapa ativa. Sem janela conhecida, a barra vai cheia. */
function progressWidth(progress?: number) {
  if (progress === undefined) return '100%';

  return `${Math.min(100, Math.max(MIN_PROGRESS_PERCENT, Math.round(progress * 100)))}%`;
}

/**
 * Prazo do rodapé pela regra do `Home.dc.html` (HOME-25): com etapa ativa, quantos dias faltam
 * para ela fechar; sem etapa ativa, quando começa a próxima; sem próxima, "etapas encerradas".
 * Sem nenhuma etapa com janela não há prazo a dizer.
 */
export function agendaDeadline(steps: PhaseStep[], now: Date = new Date()): string | null {
  if (!steps.some(step => step.finish)) return null;

  const active = steps.find(step => step.state === 'active' && step.finish);
  if (active?.finish) {
    const days = differenceInCalendarDays(new Date(active.finish), now);
    const remaining = days <= 0 ? 'encerra hoje' : days === 1 ? 'encerra em 1 dia' : `encerra em ${days} dias`;

    return `${remaining} · até ${dayMonth(active.finish)}`;
  }

  const next = steps.find(step => step.start && step.start > now.getTime());

  return next?.start ? `próxima etapa em ${dayMonth(next.start)}` : 'etapas encerradas';
}

/**
 * Legenda da agenda: "Agosto · ciclo 2S2026". O mês sai do título da etapa do calendário
 * (que pode vir em caixa alta ou com prefixo de nível); sem mês ou ciclo, não há legenda.
 */
export function agendaCaption(stageTitle?: string, cycleTitle?: string) {
  const monthName = stageTitle ? getMonthNameFromString(stageTitle) : '';
  if (!monthName || !cycleTitle) return undefined;

  return `${monthName.charAt(0).toUpperCase()}${monthName.slice(1).toLowerCase()} · ciclo ${cycleTitle}`;
}

const STATE_TEXT: Record<PhaseState, string> = {
  past: 'finalizada',
  active: 'em andamento',
  future: 'bloqueada',
};

const REACHED_TEXT = 'text-brand-blue';
const FUTURE_TEXT = 'text-content-soft';

interface PhaseAgendaProps {
  steps: PhaseStep[];
  /** Referência da agenda ao lado do título ("Agosto · ciclo 2S2026"). */
  caption?: string;
}

/**
 * "Agenda da fase": uma coluna por etapa com rótulo, barra e período. Etapas finalizadas e a
 * ativa ficam em azul; as futuras, em cinza com cadeado. A barra da etapa ativa enche conforme
 * a janela dela passa; a da finalizada fica cheia.
 */
export function PhaseAgenda({ steps, caption }: PhaseAgendaProps) {
  // Sem nenhuma etapa com data não há agenda a mostrar — só traços e barras vazias.
  if (!steps.some(step => step.dateLabel !== NO_DATE)) return null;

  // O número de colunas acompanha as etapas do programa (5 na RD, 3 na ECOA).
  const columns = { gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` };

  return (
    <div>
      <p className="mb-3.5 flex flex-wrap items-baseline gap-2.5 leading-normal">
        <span className="text-jj-label font-semibold uppercase text-content">Agenda da fase</span>
        {caption && <span className="text-jj-label text-content-soft">{caption}</span>}
      </p>

      <ol className="grid gap-1" style={columns}>
        {steps.map(step => {
          const reached = step.state !== 'future';

          return (
            <li
              key={step.label}
              aria-current={step.state === 'active' ? 'step' : undefined}
              // D3: 7px não tem degrau no Tailwind.
              className="flex min-w-0 flex-col gap-[7px]"
            >
              <span
                // D3: 5px não tem degrau no Tailwind.
                className={`flex items-center gap-[5px] text-jj-label leading-tight ${reached ? REACHED_TEXT : FUTURE_TEXT} ${
                  step.state === 'active' ? 'font-bold' : 'font-medium'
                }`}
              >
                {!reached && <FiLock aria-hidden="true" size={11} className="shrink-0" />}
                {step.label}
                <span className="screen-reader-text">
                  : {step.dateLabel === NO_DATE ? 'sem data' : step.dateLabel}, {STATE_TEXT[step.state]}
                </span>
              </span>
              {/* D3: 7px não tem degrau no Tailwind. */}
              {step.state === 'active' ? (
                <span aria-hidden="true" className="block h-[7px] overflow-hidden rounded-full bg-surface-200">
                  <span className="block h-full rounded-full bg-brand-blue" style={{ width: progressWidth(step.progress) }} />
                </span>
              ) : (
                <span aria-hidden="true" className={`block h-[7px] rounded-full ${reached ? 'bg-brand-blue' : 'bg-surface-200'}`} />
              )}
              <span aria-hidden="true" className={`text-jj-micro font-semibold ${reached ? REACHED_TEXT : FUTURE_TEXT}`}>
                {step.dateLabel}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

interface RegistrationMetaParams {
  /** "Sua realização", "Sua ação" ou "Você participa". */
  relation: string;
  /** "Nível 1" (RD) ou o indicador (ECOA). */
  detail?: string;
  created?: number;
}

/** Linha de apoio da última realização/ação: "Sua realização · Nível 1 · cadastrada em 12/08" (HOME-27). */
export function registrationMeta({ relation, detail, created }: RegistrationMetaParams): ReactNode {
  const rest = [detail, created ? `cadastrada em ${dayMonth(created)}` : undefined].filter(Boolean);

  return (
    <>
      <b className="font-semibold text-brand-blue">{relation}</b>
      {rest.map(part => ` · ${part}`).join('')}
    </>
  );
}

interface ProgramItemRowProps {
  eyebrow: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  status?: string;
  /** Rótulo lido antes do status pelo leitor de tela ("Status da entrega"). */
  statusLabel?: string;
  /** Com destino, a linha inteira vira link. */
  to?: string;
  /** Ação própria da linha, à direita. */
  action?: ReactNode;
}

/** Linha de item de programa: última realização, última ação, desafio do ciclo. */
export function ProgramItemRow({ eyebrow, title, meta, status, statusLabel, to, action }: ProgramItemRowProps) {
  const content = (
    <>
      <span className="block flex-[1_1_100%] text-jj-label font-semibold uppercase tracking-[.04em] text-content">{eyebrow}</span>

      {/* D3: min-w não tem escala de spacing no Tailwind 3.3. */}
      <span className="block min-w-[200px] flex-[1_1_240px]">
        <span className="block text-jj-body-sm font-bold leading-snug text-content">{title}</span>
        {/* D3: 3px não tem degrau no Tailwind. `text-content`, e não `text-content-soft`: em 11px sobre o cinza da linha,
            o suave dava 4,43:1 (CAN-40, apareceu com os desafios dos ciclos antigos). */}
        {meta && <span className="mt-[3px] block text-jj-label text-content">{meta}</span>}
      </span>

      {/* D3: 13px não tem degrau no Tailwind. */}
      {status && (
        <span className="ml-auto shrink-0 rounded-jj-pill bg-brand-yellow-light px-[13px] py-1.5 text-jj-micro font-bold uppercase leading-tight tracking-[.04em] text-brand-navy">
          {statusLabel && <span className="screen-reader-text">{statusLabel}: </span>}
          {status}
        </span>
      )}

      {action && <span className="ml-auto shrink-0">{action}</span>}
    </>
  );

  const rowClass = 'flex flex-wrap items-center gap-3.5 rounded-jj-lg bg-surface-100 px-4 py-3.5 text-content';

  if (to) {
    return (
      <Link
        to={to}
        className={`${rowClass} no-underline transition-colors duration-jj-fast ease-jj-standard hover:bg-surface-200 hover:text-content hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue-dark`}
      >
        {content}
      </Link>
    );
  }

  return <div className={rowClass}>{content}</div>;
}

/** Nota de prazo do rodapé, com o relógio do design. */
export function DeadlineNote({ children }: { children: ReactNode }) {
  return (
    <span
      // D3: 7px não tem degrau no Tailwind.
      className="inline-flex items-center gap-[7px] text-jj-label text-content"
    >
      <FiClock aria-hidden="true" className="shrink-0 text-brand-blue" size={14} />
      <span>{children}</span>
    </span>
  );
}

interface DraftWarningProps {
  message: string;
  onClick: () => void;
}

/**
 * Aviso pulsante de rascunho no canto de uma ação ("Reconhecer colega",
 * "Cadastrar realização"). O pai precisa ser `relative`.
 */
export function DraftWarning({ message, onClick }: DraftWarningProps) {
  return (
    <Tippy content={message} placement="top">
      <button
        type="button"
        onClick={onClick}
        aria-label={message}
        className="absolute -right-3 -top-3 rounded-full bg-brand-yellow p-1 text-brand-navy motion-safe:animate-draft-pulse"
      >
        <IoIosWarning size={18} aria-hidden="true" />
      </button>
    </Tippy>
  );
}
